import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Swal from 'sweetalert2';
import { useModal } from '../../hooks/useModal';
import { referenceService, quotationService, tenderService, companyService, authService } from '../../services/api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ProjectManagement = ({ user }) => {
  const canManage = ['Administrator', 'Manager'].includes(user?.role);
  const [projects, setProjects] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [quotations, setQuotations] = useState([]);
  const [tenders, setTenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showViewQuotationModal, setShowViewQuotationModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedQuotationForView, setSelectedQuotationForView] = useState(null);
  const [company, setCompany] = useState({});

  // Hide navbar when any modal is open
  useModal(showModal || showViewModal || showViewQuotationModal, () => {
    setShowModal(false);
    setShowViewModal(false);
    setShowViewQuotationModal(false);
  });

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'planning',
    OrganizationId: '', // Added Org Link
    startDate: '',
    endDate: '',
    expenseCost: '',
    QuotationId: '',
    TenderId: ''
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [existingDocs, setExistingDocs] = useState([]);

  const projectDocumentTypes = [
    'Letter Of Intent',
    'Purchase Order',
    'Contract Copy',
    'PO to Vendors',
    'Vendors Bills',
    'Expense Reports',
    'Email Correspondence',
    'Letter',
    'Received Letter',
    'Bill Docs',
    'Received Billed Docs',
    'Invoice(link from invoice)',
    'Sales Tax Invoice(Link from STI)',
    'DC(link from DC)',
    'Gate out(Link from Gate out)',
    'Other'
  ];

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const fileList = files.map((file, index) => ({
      id: Date.now() + index,
      name: file.name,
      size: file.size,
      type: file.type,
      file: file,
      docType: '',
      customDocType: '',
      uploadDate: new Date().toISOString().split('T')[0]
    }));
    setUploadedFiles([...uploadedFiles, ...fileList]);
  };

  const handleDocTypeChange = (fileId, type) => {
    setUploadedFiles(uploadedFiles.map(f =>
      f.id === fileId ? { ...f, docType: type, customDocType: type === 'Other' ? f.customDocType : '' } : f
    ));
  };

  const handleCustomDocTypeChange = (fileId, value) => {
    setUploadedFiles(uploadedFiles.map(f =>
      f.id === fileId ? { ...f, customDocType: value } : f
    ));
  };

  const handleExistingDocTypeChange = (docId, type) => {
    setExistingDocs(existingDocs.map(d =>
      d.id === docId ? { ...d, type: type } : d
    ));
  };

  const handleExistingCustomDocTypeChange = (docId, value) => {
    setExistingDocs(existingDocs.map(d =>
      d.id === docId ? { ...d, type: value } : d
    ));
  };

  const removeFile = (fileId) => {
    setUploadedFiles(uploadedFiles.filter(f => f.id !== fileId));
  };

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const [projRes, orgRes, quoteRes, tenderRes, companyRes] = await Promise.all([
        referenceService.getProjects(),
        referenceService.getOrgs(),
        quotationService.getAll(),
        tenderService.getAll(),
        companyService.getProfile()
      ]);
      setProjects(projRes.data);
      console.log("DEBUG: Projects Loaded:", projRes.data);
      setOrganizations(orgRes.data);
      setQuotations(quoteRes.data);
      setTenders(tenderRes.data);
      setCompany(companyRes.data || {});
      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      console.log('Submitting Project Data:', formData);

      const payload = {
        title: formData.title,
        description: formData.description,
        status: formData.status,
        OrganizationId: formData.OrganizationId === '' ? null : formData.OrganizationId,
        QuotationId: formData.QuotationId === '' ? null : formData.QuotationId,
        TenderId: formData.TenderId === '' ? null : formData.TenderId,
        startDate: formData.startDate === '' ? null : formData.startDate,
        endDate: formData.endDate === '' ? null : formData.endDate
      };

      let projectId = selectedProject?.id;

      if (selectedProject) {
        await referenceService.updateProject(selectedProject.id, payload);
      } else {
        const res = await referenceService.createProject(payload);
        projectId = res.data.id;
      }

      // Handle Multiple File Uploads
      const filesToUpload = uploadedFiles.filter(f => f.file);
      if (filesToUpload.length > 0) {
        // Validate types
        if (filesToUpload.some(d => !d.docType)) {
          Swal.fire('Error', 'Please select a Document Type for all files.', 'error');
          // Note: Project might already be created/updated. Ideally we validate before create/update.
          // But for now, we just warn and stop uploads.
          return;
        }

        for (const fileData of filesToUpload) {
          const uploadData = new FormData();
          uploadData.append('file', fileData.file);
          const finalType = fileData.docType === 'Other' ? (fileData.customDocType || 'Other') : fileData.docType;
          uploadData.append('type', finalType);
          await referenceService.uploadProjectDoc(projectId, uploadData);
        }
      }

      // Handle Existing Doc Updates
      if (selectedProject && existingDocs.length > 0) {
        for (const doc of existingDocs) {
          await referenceService.updateProjectDoc(doc.id, { type: doc.type });
        }
      }

      Swal.fire('Success', `Project ${selectedProject ? 'updated' : 'created'} successfully!`, 'success');

      setShowModal(false);
      setSelectedProject(null);
      loadProjects();
      setFormData({
        title: '',
        description: '',
        status: 'planning',
        OrganizationId: '',
        startDate: '',
        endDate: '',
        file: null
      });
      setUploadedFiles([]);
      setExistingDocs([]);
    } catch (error) {
      console.error('Project save failed:', error);
      Swal.fire('Error', 'Failed to save project', 'error');
    }
  };

  const handleEdit = (project) => {
    setSelectedProject(project);

    // Auto-detect Linked Quotation for the dropdown if not explicitly set
    let defaultQuoteId = project.QuotationId || '';
    if (!defaultQuoteId && project.Quotations && project.Quotations.length > 0) {
      // Prefer 'Awarded' status, otherwise take the first one
      const bestMatch = project.Quotations.find(q => q.status === 'Awarded') || project.Quotations[0];
      if (bestMatch) {
        defaultQuoteId = bestMatch.id;
      }
    }

    setFormData({
      title: project.title,
      description: project.description || '',
      status: project.status,
      OrganizationId: project.OrganizationId || '',
      QuotationId: defaultQuoteId,
      TenderId: project.TenderId || '',
      startDate: project.startDate || '',
      endDate: project.endDate || '',
      expenseCost: project.expenseCost || ''
    });
    setUploadedFiles([]); // Reset uploads on edit (existing docs shown separately)
    setExistingDocs(project.ProjectDocuments || []);
    setShowModal(true);
  };

  const handleViewQuotation = async (q) => {
    try {
      setLoading(true);
      const res = await quotationService.getById(q.id);
      setSelectedQuotationForView(res.data);
      setShowViewQuotationModal(true);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching quotation details:", error);
      Swal.fire('Error', 'Failed to fetch quotation details.', 'error');
      setLoading(false);
    }
  };

  const generatePDF = async (quotation, type = 'standard') => {
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      const pageWidth = doc.internal.pageSize.width;
      let yPos = 15;

      // Logo
      if (company.logo && company.logo.startsWith('data:image')) {
        try {
          const format = company.logo.includes('image/jpeg') ? 'JPEG' : 'PNG';
          doc.addImage(company.logo, format, 15, yPos, 40, 20);
        } catch (err) { console.error("Logo error:", err); }
      }

      // Distributor Logos
      let activeLogos = [];
      try {
        const pref = quotation.distributorLogo || 'default';
        let potentialLogos = [];
        if (company.distributorLogos) {
          const parsed = JSON.parse(company.distributorLogos);
          if (Array.isArray(parsed)) potentialLogos = parsed;
        }
        if (potentialLogos.length === 0 && company.distributorLogo) {
          potentialLogos.push({ src: company.distributorLogo, active: true });
        }
        if (pref === 'none') {
          activeLogos = [];
        } else if (pref === 'default' || !pref) {
          activeLogos = potentialLogos.filter(l => l.active);
        } else if (pref.startsWith('logo_')) {
          const index = parseInt(pref.replace('logo_', ''));
          if (potentialLogos[index]) activeLogos = [potentialLogos[index]];
        }
      } catch (e) { console.error("Error parsing distributorLogos", e); }

      if (activeLogos.length > 0) {
        const logoWidth = 35;
        const logoHeight = 15;
        const spacing = 2;
        const rightMargin = pageWidth - 15;
        activeLogos.reverse().forEach((logo, index) => {
          if (logo.src && logo.src.startsWith('data:image')) {
            try {
              const format = logo.src.includes('image/jpeg') ? 'JPEG' : 'PNG';
              const xPos = rightMargin - ((index + 1) * logoWidth) - (index * spacing);
              doc.addImage(logo.src, format, xPos, yPos, logoWidth, logoHeight);
            } catch (err) { console.error("Logo render error", err); }
          }
        });
      }

      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(44, 62, 80);
      doc.text(company.name || 'Company Name', pageWidth / 2, yPos + 8, { align: 'center' });

      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(100);
      const contactInfo = company.address || '';
      const contactLine = [
        company.phone ? `Tel: ${company.phone}` : '',
        company.email ? `Email: ${company.email}` : '',
        company.website ? `Web: ${company.website}` : ''
      ].filter(Boolean).join(' | ');

      let textY = yPos + 14;
      doc.text(contactInfo, pageWidth / 2, textY, { align: 'center' });
      textY += 5;
      doc.text(contactLine, pageWidth / 2, textY, { align: 'center' });
      textY += 5;

      doc.setDrawColor(200);
      doc.line(15, textY + 5, pageWidth - 15, textY + 5);

      yPos = textY + 15;
      doc.setFontSize(11);
      doc.setTextColor(44, 62, 80);
      doc.setFont(undefined, 'bold');
      doc.text('To:', 15, yPos);

      doc.setFontSize(10);
      doc.setTextColor(0);
      doc.setFont(undefined, 'bold');
      doc.text(quotation.toContact || 'Contact Person', 15, yPos + 6);
      doc.setFont(undefined, 'normal');
      doc.text(quotation.designation || '', 15, yPos + 11);
      doc.text(quotation.organization || 'Client Organization', 15, yPos + 16);

      const rightX = pageWidth - 60;
      doc.setFont(undefined, 'bold');
      doc.text(`Quotation No:`, rightX, yPos);
      doc.setFont(undefined, 'normal');
      doc.text(quotation.quoteNo || '-', rightX + 25, yPos, { align: 'left' });

      doc.setFont(undefined, 'bold');
      doc.text(`Date:`, rightX, yPos + 6);
      doc.setFont(undefined, 'normal');
      doc.text(quotation.submissionDate || '-', rightX + 25, yPos + 6, { align: 'left' });

      if (quotation.validityDate) {
        doc.setFont(undefined, 'bold');
        doc.text(`Valid Until:`, rightX, yPos + 12);
        doc.setFont(undefined, 'normal');
        doc.text(quotation.validityDate, rightX + 25, yPos + 12, { align: 'left' });
      }

      doc.setFont(undefined, 'bold');
      doc.setFontSize(11);
      doc.text(`Subject: ${quotation.subject || 'Quotation'}`, 15, yPos + 30);

      let tableColumn = ["S.No", "Product", "Model", "Description", "Qty", "Unit Price", "Total"];
      if (type === 'technical') tableColumn = ["S.No", "Product", "Model", "Description", "Qty"];

      const tableRows = [];
      const qItems = quotation.QuotationItems || [];
      qItems.forEach((item, index) => {
        let row = [
          index + 1,
          item.productName || item.description || '-',
          item.modelNumber || '-',
          item.description || '-',
          item.quantity || 0,
          parseFloat(item.unitPrice || 0).toLocaleString(),
          parseFloat(item.totalPrice || 0).toLocaleString()
        ];
        if (type === 'technical') row = row.slice(0, 5);
        tableRows.push(row);
      });

      if (type !== 'technical') {
        tableRows.push([
          { content: 'Sub Total', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
          { content: `Rs. ${parseFloat(quotation.subTotal || 0).toLocaleString()}`, styles: { halign: 'right' } }
        ]);
        if (quotation.gstAmount > 0) {
          tableRows.push([
            { content: `GST (${quotation.gstRate || 0}%)`, colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: `Rs. ${parseFloat(quotation.gstAmount || 0).toLocaleString()}`, styles: { halign: 'right' } }
          ]);
        }
        if (quotation.installationCharges > 0) {
          tableRows.push([
            { content: 'Installation/Comm:', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold' } },
            { content: `Rs. ${parseFloat(quotation.installationCharges || 0).toLocaleString()}`, styles: { halign: 'right' } }
          ]);
        }
        tableRows.push([
          { content: 'Grand Total', colSpan: 6, styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 255, 0] } },
          { content: `Rs. ${parseFloat(quotation.grandTotal || 0).toLocaleString()}`, styles: { halign: 'right', fontStyle: 'bold', fillColor: [255, 255, 0] } }
        ]);
      }

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: yPos + 35,
        theme: 'grid',
        headStyles: { fillColor: [44, 62, 80], textColor: 255, fontSize: 10, halign: 'center' },
        styles: { fontSize: 9, cellPadding: 3, valign: 'middle' },
        columnStyles: type === 'technical' ? { 0: { halign: 'center', cellWidth: 15 }, 4: { halign: 'center' } } : { 0: { halign: 'center', cellWidth: 15 }, 4: { halign: 'center' }, 5: { halign: 'right' }, 6: { halign: 'right' } },
        alternateRowStyles: { fillColor: [245, 245, 245] }
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text('Thank you for your business.', pageWidth / 2, doc.internal.pageSize.height - 10, { align: 'center' });
        doc.text(`Page ${i} of ${pageCount}`, pageWidth - 15, doc.internal.pageSize.height - 10, { align: 'right' });
      }

      doc.addPage();
      doc.setFont(undefined, 'bold');
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text('TERMS & CONDITIONS', 15, 20);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);

      const terms = (quotation.termsAndConditions || '').split('\n');
      let termY = 30;
      terms.forEach(term => {
        const splitText = doc.splitTextToSize(term, pageWidth - 30);
        doc.text(splitText, 15, termY);
        termY += (splitText.length * 4) + 2;
      });

      termY += 10;
      doc.text("Looking forward for your esteemed purchase order.", 15, termY);
      termY += 15;
      doc.text("Sincerely yours", 15, termY);

      let userSignature = '';
      try {
        const storedUser = JSON.parse(localStorage.getItem('rsms_user') || '{}');
        if (storedUser.id) {
          const uRes = await authService.getUser(storedUser.id);
          if (uRes.data && uRes.data.signature) userSignature = uRes.data.signature;
        }
      } catch (e) { console.error('Error fetching user signature for PDF', e); }

      const user = JSON.parse(localStorage.getItem('rsms_user') || '{}');
      const userName = user.name || user.username || 'Authorized Signatory';
      termY += 40;

      if (userSignature && userSignature.startsWith('data:image')) {
        try {
          const sigFormat = userSignature.includes('image/jpeg') ? 'JPEG' : 'PNG';
          doc.addImage(userSignature, sigFormat, 15, termY - 25, 40, 20);
        } catch (e) { }
      }

      doc.setFont(undefined, 'bold');
      doc.text(userName, 15, termY);
      doc.setFont(undefined, 'normal');
      doc.text(`For ${company.name || 'Company Name'}`, 15, termY + 5);

      const filenamePrefix = type === 'technical' ? 'Technical Quotation' : 'Quotation';
      doc.save(`${filenamePrefix} - ${quotation.quoteNo || 'draft'}.pdf`);
    } catch (error) {
      console.error("PDF Generation Error:", error);
      Swal.fire('Error', 'Failed to generate PDF.', 'error');
    }
  };

  const getStageBadge = (id) => {
    const stages = [
      { id: 1, name: 'Draft', color: 'secondary' },
      { id: 2, name: 'Submitted', color: 'info' },
      { id: 3, name: 'Awarded', color: 'success' },
      { id: 4, name: 'Rejected', color: 'danger' },
      { id: 5, name: 'Cancelled', color: 'dark' },
      { id: 6, name: 'Revision', color: 'warning' }
    ];
    const stage = stages.find(s => s.id === id);
    return `badge bg-${stage ? stage.color : 'secondary'}`;
  };

  const getProjectStatusBadge = (status) => {
    switch (status) {
      case 'Awarded': return 'badge bg-success';
      case 'Delivered': return 'badge bg-info';
      case 'Under Installation': return 'badge bg-warning';
      case 'Completed(Pending Performance Security)': return 'badge bg-primary';
      case 'Completed(Performance Security Released)': return 'badge bg-success';
      case 'on Hold': return 'badge bg-danger';
      case 'planning': return 'badge bg-secondary';
      case 'active': return 'badge bg-info';
      default: return 'badge bg-secondary';
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Project?',
      text: "This action cannot be undone.",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Delete'
    });
    if (result.isConfirmed) {
      try {
        await referenceService.deleteProject(id);
        Swal.fire('Deleted!', 'Project has been deleted.', 'success');
        loadProjects();
      } catch (e) {
        console.error(e);
        Swal.fire({
          icon: 'error',
          title: 'Cannot Delete Project',
          text: e.response?.data?.error || 'Failed to delete project. It may have associated records.',
        });
      }
    }
  };

  const filteredProjects = projects.filter(project =>
    !searchTerm ||
    project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (project.Organization && project.Organization.name && project.Organization.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (project.status && project.status.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-1" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: '700',
            fontSize: '2rem'
          }}>
            Project Management
          </h2>
          <p className="text-muted mb-0">Track and manage construction projects</p>
        </div>

        {canManage && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <i className="bi bi-plus-circle"></i> Create Project
          </button>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">All Projects</h5>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Organization</th>
                  <th>Status</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="5" className="text-center">Loading...</td></tr>
                ) : filteredProjects.length === 0 ? (
                  <tr><td colSpan="5" className="text-center">No projects found</td></tr>
                ) : (
                  filteredProjects.map(p => (
                    <tr key={p.id}>
                      <td>
                        <strong>{p.title}</strong>
                        {p.Quotations && p.Quotations.length > 0 && (
                          <span className="badge bg-light text-dark border ms-2" title={`${p.Quotations.length} Linked Quotation(s)`}>
                            <i className="bi bi-receipt me-1"></i> {p.Quotations.length}
                          </span>
                        )}
                      </td>
                      <td>{p.Organization ? p.Organization.name : '-'}</td>
                      <td>
                        <span className={getProjectStatusBadge(p.status)}>
                          {p.status}
                        </span>
                      </td>
                      <td>{p.startDate ? new Date(p.startDate).toLocaleDateString() : '-'}</td>
                      <td>{p.endDate ? new Date(p.endDate).toLocaleDateString() : '-'}</td>
                      <td>{p.description}</td>
                      <td>
                        {canManage && (
                          <>
                            <button className="btn btn-sm btn-outline-primary me-2" onClick={() => handleEdit(p)}>
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button className="btn btn-sm btn-outline-danger me-2" onClick={() => handleDelete(p.id)}>
                              <i className="bi bi-trash"></i>
                            </button>
                          </>
                        )}
                        <button className="btn btn-sm btn-outline-info" onClick={() => {
                          setSelectedProject(p);
                          setShowViewModal(true);
                        }}>
                          <i className="bi bi-eye"></i>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {
        showModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{selectedProject ? 'Edit Project' : 'Create New Project'}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowModal(false)}>
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Project Title *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Client/Organization</label>
                      <select
                        className="form-select"
                        value={formData.OrganizationId}
                        onChange={(e) => setFormData({ ...formData, OrganizationId: e.target.value })}
                      >
                        <option value="">Select Client</option>
                        {organizations.map(org => (
                          <option key={org.id} value={org.id}>{org.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Description</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Link Quotation (For Revenue/P&L)</label>
                      <select
                        className="form-select"
                        value={formData.QuotationId}
                        onChange={(e) => setFormData({ ...formData, QuotationId: e.target.value })}
                      >
                        <option value="">Select Associated Quotation (Optional)</option>
                        {quotations.filter(q => q.status === 'Awarded' || q.status === 'Submitted').map(q => (
                          <option key={q.id} value={q.id}>{q.quoteNo} - {q.title} (Rs. {q.grandTotal || q.value})</option>
                        ))}
                      </select>
                      <div className="form-text">Linking a quotation allows automated revenue calculation.</div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Link Tender (For Revenue/P&L)</label>
                      <select
                        className="form-select"
                        value={formData.TenderId}
                        onChange={(e) => setFormData({ ...formData, TenderId: e.target.value })}
                        disabled={!!formData.QuotationId}
                      >
                        <option value="">Select Associated Tender (Optional)</option>
                        {tenders.filter(t => t.status === 'active' || t.status === 'closed').map(t => (
                          <option key={t.id} value={t.id}>{t.tenderNo} - {t.title} (Rs. {t.value})</option>
                        ))}
                      </select>
                      <div className="form-text">Select Tender if no Quotation is linked.</div>
                    </div>


                    <div className="row g-3 mb-3">
                      <div className="col-md-4">
                        <label className="form-label">Status</label>
                        <select
                          className="form-select"
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        >
                          <option value="Awarded">Awarded</option>
                          <option value="Delivered">Delivered</option>
                          <option value="Under Installation">Under Installation</option>
                          <option value="Completed(Pending Performance Security)">Completed(Pending Performance Security)</option>
                          <option value="Completed(Performance Security Released)">Completed(Performance Security Released)</option>
                          <option value="on Hold">on Hold</option>
                          <option value="planning">planning</option>
                          <option value="active">active</option>
                        </select>
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Start Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.startDate}
                          onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">End Date</label>
                        <input
                          type="date"
                          className="form-control"
                          value={formData.endDate}
                          onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                        />
                      </div>
                    </div>

                    {/* ATTACHMENT SECTION */}
                    <div className="mb-3">
                      <label className="form-label">Attachments</label>
                      <input
                        type="file"
                        className="form-control"
                        multiple
                        onChange={handleFileChange}
                      />
                      <div className="form-text">Upload relevant project documents</div>

                      {uploadedFiles.length > 0 && (
                        <div className="mt-2">
                          <strong>Selected Files:</strong>
                          <ul className="list-group mt-2">
                            {uploadedFiles.map((file) => (
                              <li key={file.id} className="list-group-item">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <span>
                                    <i className="bi bi-file-earmark"></i> {file.name}
                                    <small className="text-muted ms-2">({(file.size / 1024).toFixed(2)} KB)</small>
                                  </span>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => removeFile(file.id)}
                                  >
                                    <i className="bi bi-x"></i>
                                  </button>
                                </div>
                                <div className="row g-2">
                                  <div className="col-md-6">
                                    <select
                                      className="form-select form-select-sm"
                                      value={file.docType}
                                      onChange={(e) => handleDocTypeChange(file.id, e.target.value)}
                                    >
                                      <option value="">Select Document Type *</option>
                                      {projectDocumentTypes.map((type, idx) => (
                                        <option key={idx} value={type}>{type}</option>
                                      ))}
                                    </select>
                                  </div>
                                  {file.docType === 'Other' && (
                                    <div className="col-md-6">
                                      <input
                                        type="text"
                                        className="form-control form-select-sm"
                                        placeholder="Specify Document Type"
                                        value={file.customDocType}
                                        onChange={(e) => handleCustomDocTypeChange(file.id, e.target.value)}
                                      />
                                    </div>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>

                    {/* EXISTING DOCUMENTS LIST (Edit Mode Only) */}
                    {selectedProject && existingDocs.length > 0 && (
                      <div className="mt-3">
                        <h6 className="fw-bold">Attached Documents:</h6>
                        <ul className="list-group">
                          {existingDocs.map(doc => (
                            <li key={doc.id} className="list-group-item">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <a href={`http://localhost:5000${doc.filePath}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                  <i className="bi bi-file-earmark-text me-2"></i>{doc.name}
                                </a>
                                <button type="button" className="btn btn-sm btn-outline-danger" onClick={async () => {
                                  if (await Swal.fire({ title: 'Delete?', html: `Delete document <b>${doc.name}</b>?`, showCancelButton: true }).then(r => r.isConfirmed)) {
                                    try {
                                      await referenceService.deleteProjectDoc(doc.id);
                                      // Refresh logic
                                      const res = await referenceService.getProjects();
                                      setProjects(res.data);
                                      const updatedP = res.data.find(p => p.id === selectedProject.id);
                                      // Update both selectedProject and local existingDocs
                                      if (updatedP) {
                                        setSelectedProject(updatedP);
                                        setExistingDocs(updatedP.ProjectDocuments || []);
                                      }
                                    } catch (err) { Swal.fire('Error', 'Delete failed', 'error'); }
                                  }
                                }}>
                                  <i className="bi bi-trash"></i>
                                </button>
                              </div>
                              <div className="row g-2">
                                <div className="col-md-6">
                                  <select
                                    className="form-select form-select-sm"
                                    value={projectDocumentTypes.includes(doc.type) ? doc.type : 'Other'}
                                    onChange={(e) => handleExistingDocTypeChange(doc.id, e.target.value)}
                                  >
                                    <option value="">Select Document Type</option>
                                    {projectDocumentTypes.map((type, idx) => (
                                      <option key={idx} value={type}>{type}</option>
                                    ))}
                                  </select>
                                </div>
                                {(!projectDocumentTypes.includes(doc.type) || doc.type === 'Other') && (
                                  <div className="col-md-6">
                                    <input
                                      type="text"
                                      className="form-control form-select-sm"
                                      placeholder="Specify Document Type"
                                      value={doc.type === 'Other' ? '' : doc.type}
                                      onChange={(e) => handleExistingCustomDocTypeChange(doc.id, e.target.value)}
                                    />
                                  </div>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => { setShowModal(false); setSelectedProject(null); }}>Cancel</button>
                    <button type="submit" className="btn btn-primary">{selectedProject ? 'Update' : 'Create'} Project</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }

      {/* View Modal */}
      {
        showViewModal && selectedProject && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Project Details: {selectedProject.title}</h5>
                  <button type="button" className="btn-close" onClick={() => {
                    setShowViewModal(false);
                    setSelectedProject(null);
                  }}></button>
                </div>
                <div className="modal-body">
                  {/* DETAILS SECTION */}
                  <div className="mb-4">
                    <h6 className="fw-bold border-bottom pb-2">Overview</h6>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="fw-bold">Title:</label>
                        <p>{selectedProject.title}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="fw-bold">Status:</label>
                        <p><span className="badge bg-info">{selectedProject.status}</span></p>
                      </div>
                    </div>
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="fw-bold">Client/Organization:</label>
                        <p>{selectedProject.Organization ? selectedProject.Organization.name : 'N/A'}</p>
                      </div>
                      <div className="col-md-6">
                        <label className="fw-bold">Duration:</label>
                        <p>
                          {selectedProject.startDate ? new Date(selectedProject.startDate).toLocaleDateString() : 'TBD'}
                          {' - '}
                          {selectedProject.endDate ? new Date(selectedProject.endDate).toLocaleDateString() : 'TBD'}
                        </p>
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="fw-bold">Description:</label>
                      <p className="border p-2 rounded bg-light">{selectedProject.description || 'No description provided.'}</p>
                    </div>


                  </div>

                  {/* LINKED QUOTATIONS SECTION */}
                  <div className="mb-4">
                    <h6 className="fw-bold border-bottom pb-2">Linked Quotations</h6>
                    {(!selectedProject.Quotations || selectedProject.Quotations.length === 0) ? (
                      <p className="text-muted fst-italic">No quotations linked to this project.</p>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th>Quote No</th>
                              <th>Title</th>
                              <th>Status</th>
                              <th>Value</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedProject.Quotations.map(q => (
                              <tr key={q.id}>
                                <td>{q.quoteNo}</td>
                                <td>{q.title}</td>
                                <td>
                                  <span className={getStageBadge(q.currentStage)}>
                                    {q.stageName || q.status}
                                  </span>
                                </td>
                                <td className="text-end">
                                  {q.grandTotal ? parseFloat(q.grandTotal).toLocaleString() : parseFloat(q.value || 0).toLocaleString()}
                                </td>
                                <td>
                                  <button className="btn btn-sm btn-outline-info me-1" onClick={() => handleViewQuotation(q)} title="View Preview">
                                    <i className="bi bi-eye"></i>
                                  </button>
                                  <button className="btn btn-sm btn-outline-dark" onClick={async () => {
                                    try {
                                      const res = await quotationService.getById(q.id);
                                      generatePDF(res.data, 'standard');
                                    } catch (err) {
                                      Swal.fire('Error', 'Failed to fetch quotation for PDF.', 'error');
                                    }
                                  }} title="Download PDF">
                                    <i className="bi bi-file-pdf"></i>
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* DOCUMENTS SECTION */}
                  <div>
                    <h6 className="fw-bold border-bottom pb-2">Documents</h6>

                    {(!selectedProject.ProjectDocuments || selectedProject.ProjectDocuments.length === 0) ? (
                      <p className="text-muted">No documents attached.</p>
                    ) : (
                      <div className="list-group">
                        {selectedProject.ProjectDocuments.map(doc => (
                          <div key={doc.id} className="list-group-item d-flex justify-content-between align-items-center">
                            <div>
                              <a href={`http://localhost:5000${doc.filePath}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                                <i className="bi bi-file-earmark-text me-2"></i>{doc.name}
                              </a>
                              {/* Assuming size isn't stored for project docs yet based on previous code, but distinct from Tenders. 
                                  If size isn't available, we just omit it or show default. 
                                  The user screenshot shows (752.32 KB) for Tenders. 
                                  The Project doc object has `createdAt`. 
                                  I will stick to the requested structure: [Icon] Filename (Size if avail)    TYPE
                               */}
                            </div>
                            <span className="badge bg-light text-dark border text-uppercase">{doc.type}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => {
                    setShowViewModal(false);
                    setSelectedProject(null);
                  }}>Close</button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* View Quotation Preview Modal */}
      {
        showViewQuotationModal && selectedQuotationForView && createPortal(
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1070 }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Quotation Preview - {selectedQuotationForView.quoteNo}</h5>
                  <button type="button" className="btn-close" onClick={() => setShowViewQuotationModal(false)}><i className="bi bi-x-lg"></i></button>
                </div>
                <div className="modal-body p-4 bg-light">
                  <div className="card border-0 shadow-sm" id="quotation-preview">
                    <div className="card-body p-5" style={{ backgroundColor: 'white' }}>
                      {/* Header */}
                      <div className="d-flex justify-content-between align-items-center mb-5 border-bottom pb-4">
                        <div>
                          <h2 className="fw-bold text-primary mb-1">{company.name || 'Company Name'}</h2>
                          <div className="text-muted small">
                            {company.address}<br />
                            Phone: {company.phone} | Email: {company.email}
                          </div>
                        </div>
                        <div className="text-end">
                          <h4 className="fw-bold">QUOTATION</h4>
                          <span className={getStageBadge(selectedQuotationForView.currentStage)}>{selectedQuotationForView.stageName}</span>
                        </div>
                      </div>

                      {/* Info Grid */}
                      <div className="row mb-5">
                        <div className="col-md-6">
                          <h6 className="text-uppercase text-muted small fw-bold mb-3">Ref/Date</h6>
                          <table className="table table-sm table-borderless">
                            <tbody>
                              <tr>
                                <th className="ps-0" style={{ width: '100px' }}>Quote No:</th>
                                <td>{selectedQuotationForView.quoteNo}</td>
                              </tr>
                              <tr>
                                <th className="ps-0">Date:</th>
                                <td>{selectedQuotationForView.submissionDate}</td>
                              </tr>
                              <tr>
                                <th className="ps-0">Valid Until:</th>
                                <td>{selectedQuotationForView.validityDate}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div className="col-md-6">
                          <h6 className="text-uppercase text-muted small fw-bold mb-3">Customer</h6>
                          <div className="fw-bold">{selectedQuotationForView.toContact || 'Contact Person'}</div>
                          <div>{selectedQuotationForView.designation}</div>
                          <div>{selectedQuotationForView.organization}</div>
                        </div>
                      </div>

                      {/* Subject */}
                      <div className="mb-4">
                        <h6 className="text-uppercase text-muted small fw-bold">Subject</h6>
                        <p className="fw-bold fs-5">{selectedQuotationForView.subject}</p>
                      </div>

                      {/* Items Table */}
                      <div className="table-responsive mb-4">
                        <table className="table table-striped table-bordered text-dark">
                          <thead>
                            <tr style={{ backgroundColor: '#2c3e50', color: 'white' }}>
                              <th style={{ width: '5%', color: 'white' }}>#</th>
                              <th style={{ width: '25%', color: 'white' }}>Product</th>
                              <th style={{ width: '15%', color: 'white' }}>Model</th>
                              <th style={{ width: '30%', color: 'white' }}>Description</th>
                              <th style={{ width: '5%', color: 'white' }} className="text-center">Qty</th>
                              <th style={{ width: '10%', color: 'white' }} className="text-end">Unit Price</th>
                              <th style={{ width: '10%', color: 'white' }} className="text-end">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedQuotationForView.QuotationItems || selectedQuotationForView.items || []).map((item, idx) => (
                              <tr key={idx}>
                                <td>{idx + 1}</td>
                                <td className="fw-bold">{item.productName}</td>
                                <td>{item.modelNumber}</td>
                                <td style={{ whiteSpace: 'pre-line' }}>{item.description}</td>
                                <td className="text-center">{item.quantity}</td>
                                <td className="text-end">{parseFloat(item.unitPrice || 0).toLocaleString()}</td>
                                <td className="text-end">{parseFloat(item.totalPrice || (item.unitPrice * item.quantity) || 0).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Totals */}
                      <div className="row justify-content-end mb-4">
                        <div className="col-md-5 col-lg-4">
                          <table className="table table-sm table-borderless">
                            <tbody>
                              <tr>
                                <td className="fw-bold text-end">Sub Total:</td>
                                <td className="text-end w-50">{parseFloat(selectedQuotationForView.subTotal).toLocaleString()}</td>
                              </tr>
                              {selectedQuotationForView.gstAmount > 0 && (
                                <tr>
                                  <td className="fw-bold text-end">GST ({selectedQuotationForView.gstRate}%):</td>
                                  <td className="text-end">{parseFloat(selectedQuotationForView.gstAmount).toLocaleString()}</td>
                                </tr>
                              )}
                              {selectedQuotationForView.installationCharges > 0 && (
                                <tr>
                                  <td className="fw-bold text-end">Installation:</td>
                                  <td className="text-end">{parseFloat(selectedQuotationForView.installationCharges).toLocaleString()}</td>
                                </tr>
                              )}
                              <tr className="border-top border-dark">
                                <td className="fw-bold text-end fs-5">Grand Total:</td>
                                <td className="fw-bold text-end fs-5 text-primary">Rs. {parseFloat(selectedQuotationForView.grandTotal).toLocaleString()}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Terms */}
                      {selectedQuotationForView.termsAndConditions && (
                        <div className="border-top pt-4">
                          <h6 className="text-uppercase text-muted small fw-bold mb-2">Terms & Conditions</h6>
                          <div className="small text-muted" style={{ whiteSpace: 'pre-line' }}>
                            {selectedQuotationForView.termsAndConditions}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer bg-light">
                  <button type="button" className="btn btn-secondary" onClick={() => setShowViewQuotationModal(false)}>Close Preview</button>
                  <button type="button" className="btn btn-primary" onClick={() => generatePDF(selectedQuotationForView)}><i className="bi bi-file-pdf me-1"></i> Download PDF</button>
                </div>
              </div>
            </div>
          </div >, document.body
        )
      }

    </div >
  );
};

export default ProjectManagement;
